import * as React from "react";
import { Modal } from "antd";
import { useFormContext } from "react-hook-form";
import { NGButton } from "../../newcomponents/button/Button";
import { FormDryRunAlert } from "./create_ng/formDryRunAlert";
import { NGLabelText } from "../../newcomponents/text/labelText";
import { MinusCircleOutlined } from "@ant-design/icons";
import { FormNGInput } from "./create_ng/components/FormNGInput";
import { FormNGKindSelect } from "./create_ng/components/FormNGKindSelect";
import { DomainInput, DomainPortInput } from "./create_ng/types/input";
import { Workload } from "../../schema/types/workload/workload";
import { v4 as uuidv4 } from "uuid";
import { linksOf } from "../../services/cpln";
import { ngParseLink } from "../../utils/linkParser/linkParser";
import clsx from "clsx";

interface Props {
  portId: string;
  portIndex: number;
  onClose: () => void;
}
export const DomainPortRouteEditMirrorModal: React.FC<Props> = ({ portId, portIndex, onClose }) => {
  const { control, watch, setValue, getValues } = useFormContext<DomainInput>();
  const fPorts: DomainPortInput[] = watch("advanced.spec.ports");
  const fPort: DomainPortInput = React.useMemo(() => {
    return fPorts.find((p) => p.id === portId)!;
  }, [fPorts, portId]);

  if (!fPort) {
    return null;
  }

  return (
    <Modal
      open={true}
      styles={{ body: { height: "70vh", padding: 0 } }}
      width={1000}
      title={`Edit Route Mirrors for Port ${fPort.number}`}
      onCancel={() => {}}
      maskClosable={false}
      closable={false}
      footer={
        <div className="modal-actions">
          <NGButton
            variant="primary"
            onClick={() => {
              onClose();
            }}
          >
            OK
          </NGButton>
        </div>
      }
    >
      <div className="h-full overflow-auto">
        {fPort.routes.map((route, routeIndex) => {
          return (
            <div key={route.id}>
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-2">
                  <NGLabelText className="cursor-default">{`Workload: ${route.workload.name} ${
                    route.prefixType === "prefix" ? `Prefix: ${route.prefix}` : `Regex: ${route.regex}`
                  }`}</NGLabelText>
                  <NGButton
                    variant="primary"
                    size={"small"}
                    onClick={() => {
                      const mirrors = getValues(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror`);
                      setValue(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror` as const, [
                        ...mirrors,
                        { id: uuidv4(), workload: { name: "", link: "" }, percent: "100" },
                      ]);
                    }}
                  >
                    Add
                  </NGButton>
                </div>
                {route.mirror.length < 1 ? <div>No Items</div> : null}
                {route.mirror.map((mirror, mirrorIndex) => {
                  return (
                    <div key={mirror.id} className="flex items-center gap-2 mb-1">
                      <FormNGKindSelect<Workload>
                        name={
                          `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.workload.link` as const
                        }
                        control={control}
                        rules={{ required: "Workload is required" }}
                        onChangeExtra={(value, item) => {
                          const { name } = ngParseLink(value, { useInputCtx: true });
                          setValue(
                            `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.workload.name` as const,
                            name,
                          );
                          setValue(
                            `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.workload.item` as const,
                            item,
                          );
                        }}
                        kind="workload"
                        optionValueAs="absolute"
                        style={{ minWidth: 400, maxWidth: 400 }}
                        renderOption={({
                          option,
                          getOptionLabel,
                          getOptionValue,
                          isHovered,
                          isSelected,
                          isDisabled,
                          isLabel,
                          props,
                        }) => {
                          const gvcLink = linksOf(option).gvc!;
                          const { name: optionGvcName } = ngParseLink(gvcLink, { useInputCtx: true });
                          return (
                            <li
                              key={getOptionValue(option)}
                              {...props}
                              className={clsx(`flex items-center color-default justify-between`, {
                                "option-hover": isHovered,
                                "option-selected": isSelected,
                                "option-label": isLabel,
                                "option-disabled": isDisabled,
                                "cursor-not-allowed": isLabel || isDisabled,
                              })}
                            >
                              <span className="truncate">{getOptionLabel(option)}</span>
                              <div className="flex items-center gap-1 text-sm">
                                <NGLabelText className="text-sm">GVC</NGLabelText>
                                <span>{optionGvcName}</span>
                              </div>
                            </li>
                          );
                        }}
                      />
                      <FormNGInput
                        name={
                          `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.percent` as const
                        }
                        control={control}
                        required={true}
                        style={{ minWidth: 100, maxWidth: 100 }}
                      />
                      <MinusCircleOutlined
                        className="color-danger"
                        onClick={() => {
                          const mirrors = getValues(
                            `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror`,
                          );
                          const newMirrors = mirrors.filter((_, idx) => idx !== mirrorIndex);
                          setValue(
                            `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror` as const,
                            newMirrors,
                          );
                        }}
                      />
                    </div>
                  );
                })}
                <FormDryRunAlert
                  path={`spec.ports[${portIndex}].routes[${routeIndex}].mirror`}
                  paths={[
                    ...route.mirror.map(
                      (_, i) => `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${i}]`,
                    ),
                    ...route.mirror.map(
                      (_, i) => `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${i}].workloadLink`,
                    ),
                    ...route.mirror.map(
                      (_, i) => `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${i}].percent`,
                    ),
                  ]}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
